import { NetworkStatus } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import { SearchQueryOrder, SearchQueryRequestedPage } from "types/generated";

import {
  USER_SESSIONS_WITH_ACCESS_TO_SPACE,
  USER_SESSIONS_WITH_ACCESS_TO_SPACE_COUNT,
} from "./gql";
import { SpaceDetailsUserSession } from "./types";

type UseSpaceDetailsUserSessionsProps = {
  spaceId: string;
  orderBy: SearchQueryOrder;
  first: number;
  fullTextSearch: string | null;
};

const useSpaceDetailsUserSessions = ({
  spaceId,
  orderBy,
  first,
  fullTextSearch,
}: UseSpaceDetailsUserSessionsProps) => {
  const [requestedPage, setRequestedPage] = useState<SearchQueryRequestedPage | null>(null);
  const [after, setAfter] = useState<string | null>(null);

  const resetKey = `${spaceId}:${orderBy.field}:${orderBy.direction}:${fullTextSearch ?? ""}`;
  const [previousResetKey, setPreviousResetKey] = useState(resetKey);

  if (previousResetKey !== resetKey) {
    setPreviousResetKey(resetKey);
    setRequestedPage(null);
    setAfter(null);
  }

  const variables = useMemo(
    () => ({
      space: spaceId,
      input: {
        predicates: [],
        orderBy: [orderBy],
        fullTextSearch,
        first,
        after,
        requestedPage,
      },
    }),
    [spaceId, orderBy, first, after, requestedPage, fullTextSearch]
  );

  const { error, loading, data, networkStatus, previousData } = usePolledQuery(
    USER_SESSIONS_WITH_ACCESS_TO_SPACE,
    {
      variables,
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
    }
  );

  const { data: countData, previousData: previousCountData } = usePolledQuery(
    USER_SESSIONS_WITH_ACCESS_TO_SPACE_COUNT,
    {
      variables: { space: spaceId, input: { fields: [], fullTextSearch, predicates: [] } },
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
    }
  );

  const loadUsers = useCallback(
    ({
      cursor,
      requestedPage,
    }: {
      cursor: string | null;
      requestedPage: SearchQueryRequestedPage | null;
    }) => {
      setAfter(cursor);
      setRequestedPage(requestedPage);
    },
    []
  );

  const initialLoading =
    loading && networkStatus === NetworkStatus.loading && !data?.userSessionsWithAccessToSpace;

  const loadingContent =
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.setVariables);

  const users = useMemo<SpaceDetailsUserSession[]>(() => {
    const edges =
      data?.userSessionsWithAccessToSpace?.edges ||
      previousData?.userSessionsWithAccessToSpace?.edges ||
      [];

    return edges.map(({ node }) => ({
      // login is the backend's grouping key and paging tiebreaker: unique per page and stable
      // across polls. There is no id to fall back on.
      id: node.user.login,
      name: node.user.login,
      roleBindings: node.roles.map((grant) => ({
        // The backend collapses grants to one per role id today, so role.id alone would do; keying
        // on the origin too keeps this correct if that relaxes, and matches the IAM id shape.
        id: `${grant.origin}:${grant.role.id}`,
        role: { id: grant.role.id, name: grant.role.name, isSystem: grant.role.isSystem },
        origin: grant.origin,
      })),
    }));
  }, [
    data?.userSessionsWithAccessToSpace?.edges,
    previousData?.userSessionsWithAccessToSpace?.edges,
  ]);

  const totalCount =
    countData?.userSessionsWithAccessToSpaceSuggestions.filteredCount ??
    previousCountData?.userSessionsWithAccessToSpaceSuggestions.filteredCount ??
    0;

  return {
    loadUsers,
    error,
    users,
    initialLoading,
    loadingContent,
    totalCount,
    endCursor: data?.userSessionsWithAccessToSpace?.pageInfo.endCursor,
    startCursor: data?.userSessionsWithAccessToSpace?.pageInfo.startCursor,
    paginationResetKey: resetKey,
  };
};

export default useSpaceDetailsUserSessions;
